import { create } from 'zustand';
import type { ExportTask, ExportFormat } from '@/types';
import { v4 as uuid } from 'uuid';

interface ExportState {
  tasks: ExportTask[];
  currentTaskId: string | null;
  isExporting: boolean;
}

interface ExportActions {
  // Task management
  createTask: (creativeId: string, format: ExportFormat) => ExportTask;
  updateTaskProgress: (taskId: string, progress: number) => void;
  completeTask: (taskId: string, result: Blob) => void;
  failTask: (taskId: string, error: string) => void;
  cancelTask: (taskId: string) => void;
  clearCompletedTasks: () => void;

  // Get task
  getTask: (taskId: string) => ExportTask | undefined;
}

export const useExportStore = create<ExportState & ExportActions>((set, get) => ({
  tasks: [],
  currentTaskId: null,
  isExporting: false,

  createTask: (creativeId, format) => {
    const task: ExportTask = {
      id: uuid(),
      creativeId,
      format,
      status: 'pending',
      progress: 0,
      createdAt: new Date().toISOString(),
    };
    set((s) => ({
      tasks: [...s.tasks, task],
      currentTaskId: task.id,
      isExporting: true,
    }));
    return task;
  },

  updateTaskProgress: (taskId, progress) => {
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === taskId
          ? { ...t, status: 'processing' as const, progress }
          : t
      ),
    }));
  },

  completeTask: (taskId, result) => {
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === taskId
          ? { ...t, status: 'completed' as const, progress: 100, result }
          : t
      ),
      currentTaskId: s.currentTaskId === taskId ? null : s.currentTaskId,
      isExporting: s.tasks.some(
        (t) => t.id !== taskId && (t.status === 'pending' || t.status === 'processing')
      ),
    }));
  },

  failTask: (taskId, error) => {
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === taskId
          ? { ...t, status: 'failed' as const, error }
          : t
      ),
      currentTaskId: s.currentTaskId === taskId ? null : s.currentTaskId,
      isExporting: s.tasks.some(
        (t) => t.id !== taskId && (t.status === 'pending' || t.status === 'processing')
      ),
    }));
  },

  cancelTask: (taskId) => {
    set((s) => ({
      tasks: s.tasks.filter((t) => t.id !== taskId),
      currentTaskId: s.currentTaskId === taskId ? null : s.currentTaskId,
      isExporting: s.tasks.some(
        (t) => t.id !== taskId && (t.status === 'pending' || t.status === 'processing')
      ),
    }));
  },

  clearCompletedTasks: () => {
    set((s) => ({
      tasks: s.tasks.filter((t) => t.status !== 'completed' && t.status !== 'failed'),
    }));
  },

  getTask: (taskId) => {
    return get().tasks.find((t) => t.id === taskId);
  },
}));
